import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { type FC, useEffect, useRef, useState } from 'react';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { SessionAction } from '@/constants';
import { useCurrentUser, useView } from '@/hooks';
import {
  isAdmin,
  isMomentsSession,
  NoteOwnerType,
  SessionStatus,
  SessionWarningAction,
} from '@/types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Icon,
  Link,
  Paper,
  Typography,
} from '@/ui';
import { track } from '@/utils';

import Header from './Header';
import SessionWarning from './SessionWarning';

import type {
  MomentsMemberArtifactSummary,
  MomentsSessionTranscriptSummary,
} from '@/store/api/momentsApi';
import type { Session, User } from '@/types';

import CancelSession from '@/components/Cal/cancelSession';
import RescheduleSession from '@/components/Cal/rescheduleSession';
import { SlackishMarkdown } from '@/components/Markdown';
import Notes from '@/components/Notes/Notes';
import {
  CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG,
  isMemberReflectionProfile,
  MEMBER_REFLECTION_PROFILE_SLUGS,
  memberReflectionDescription,
  memberReflectionLabel,
} from '@/components/Sessions/memberReflectionProfiles';
import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { useSessionJoinUrl } from '@/features/appointments/useSessionJoinUrl';
import {
  type SessionActionEvent,
  sessionNeedsActionWarning,
  useSessionActions,
} from '@/features/coachSchedule/useSessionActions';
import {
  buildMemberSessionTranscriptPath,
  readyTranscriptItems,
} from '@/pages/MemberSessionTranscript/transcriptUtils';
import { api } from '@/store/api';
import {
  findMomentsMemberByEmail,
  useGetMomentsCoachMembersQuery,
  useGetMomentsCoachSessionArtifactsQuery,
  useGetMomentsSessionArtifactsQuery,
  useGetMomentsSessionTranscriptsQuery,
} from '@/store/api/momentsApi';
import { useUpdateSessionMutation } from '@/store/api/sessionApi';
import { useGetSharedArtifactBySessionQuery } from '@/store/api/sharedArtifactApi';
import { useGetUserQuery } from '@/store/api/userApi';
import { useAppDispatch } from '@/store/hooks';
import { toDateSlug } from '@/utils/dateSlug';

interface Props {
  hideMenu?: boolean;
  isCoachViewing: boolean;
  loading?: boolean;
  onViewProfile?: () => void;
  otherUser: Pick<
    User,
    'avatarUrl' | 'email' | 'firstName' | 'id' | 'lastName'
  >;
  session: Session;
}

// Session details and Force Cancel drive Cal.com flows. For MentoCal coaches,
// Ops acts from the member session list instead (MEN-5338); the coach and
// member keep the panel because its buttons open the native flows. Ops sees
// neither until the coach's calendar is known, so the Cal.com controls don't
// flash in and out.
const calComControlsVisibility = ({
  isAdminViewer,
  isCoachCalendarKnown,
  isCoachOnMentoCal,
  isThirdPartyViewer,
}: {
  isAdminViewer: boolean;
  isCoachCalendarKnown: boolean;
  isCoachOnMentoCal: boolean;
  isThirdPartyViewer: boolean;
}) => {
  const isCalComCoach = isCoachCalendarKnown && !isCoachOnMentoCal;
  return {
    showForceCancel: isAdminViewer && isCalComCoach,
    showSessionDetails: !isThirdPartyViewer || isCalComCoach,
  };
};

const memberCanUseNativeMomentsCancel = (
  user: User,
  session: Session,
  isCoachViewing: boolean,
) => !isCoachViewing && user.id === session.userId;

const memberCanUseNativeMomentsReschedule = (
  user: User,
  session: Session,
  isCoachViewing: boolean,
) =>
  memberCanUseNativeMomentsCancel(user, session, isCoachViewing) &&
  (Boolean(session.sourceAppointmentId) || isMomentsSession(session));

const openSessionAction = ({
  coachAction,
  legacyAction,
  useCoachAction,
}: {
  coachAction: () => void;
  legacyAction: () => void;
  useCoachAction: boolean;
}) => {
  if (useCoachAction) {
    coachAction();
    return;
  }
  legacyAction();
};

const trackCoachSessionAction = (
  { action, isWarning, session }: SessionActionEvent,
  isCoach: boolean,
) => {
  if (action === 'cancel') {
    track(isWarning ? 'Shown Cancel Speedbump' : 'Clicked Cancel Session', {
      bookId: session.bookId,
      ...(isWarning ? { isCoach } : {}),
      sessionId: session.id,
    });
    return;
  }
  track(isWarning ? 'Shown Reschedule Speedbump' : 'Click Reschedule Session', {
    bookId: session.bookId,
    ...(isWarning ? { isCoach } : {}),
    sessionId: session.id,
  });
};

const ForceCancelDialog = ({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogContent>
      <Typography variant="body1">
        Warning: Only force cancel if the session is already canceled in
        Cal.com. Otherwise, please use the regular cancel flow.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button color="warning" variant="contained" onClick={onConfirm}>
        Force Cancel
      </Button>
    </DialogActions>
  </Dialog>
);

const profileLabel = (profileSlug: string) =>
  profileSlug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const reflectionArtifactPath = (
  artifact: MomentsMemberArtifactSummary,
  options: { coachShell?: boolean } = {},
) => {
  const dateSlug = toDateSlug(
    artifact.sessions[0]?.plannedStartAt || artifact.updatedAt,
    'reflection',
  );
  const basePath = options.coachShell ? '/coach' : '/m';
  return `${basePath}/artifacts/session-reflection/${artifact.artifactId}/${dateSlug}`;
};

const SESSION_ARTIFACT_PROFILE_SLUGS = new Set([
  ...MEMBER_REFLECTION_PROFILE_SLUGS,
  'coaching-feedback',
]);

const sessionArtifactDescription = (profileSlug: string) => {
  if (isMemberReflectionProfile(profileSlug)) {
    return memberReflectionDescription(profileSlug);
  }
  if (profileSlug === 'coaching-feedback') {
    return 'View coaching reflection';
  }
  return 'View Moments artifact';
};

const SessionArtifacts = ({
  artifacts,
  coachShell = false,
}: {
  artifacts: MomentsMemberArtifactSummary[];
  coachShell?: boolean;
}) => {
  const sessionArtifacts = artifacts.filter((artifact) =>
    SESSION_ARTIFACT_PROFILE_SLUGS.has(artifact.profileSlug),
  );

  if (sessionArtifacts.length === 0) {
    return null;
  }

  const isReflectionOnly = sessionArtifacts.every((artifact) =>
    isMemberReflectionProfile(artifact.profileSlug),
  );
  const reflectionHeading =
    isReflectionOnly &&
    sessionArtifacts.every(
      (artifact) =>
        artifact.profileSlug === CHEM_CALL_REFLECTION_MEMBER_PROFILE_SLUG,
    )
      ? 'Chemistry Call Reflection'
      : 'Session reflection';

  return (
    <Paper className="mt-6 px-8 pb-8 pt-6" variant="outlined">
      <Typography className="mb-4 font-bold" variant="h5">
        {isReflectionOnly ? reflectionHeading : 'Moments artifacts'}
      </Typography>
      <div className="flex flex-col gap-3">
        {sessionArtifacts.map((artifact) => (
          <Link
            key={artifact.artifactId}
            className="flex items-center justify-between rounded-xl border border-solid border-cement-200 px-4 py-3 no-underline"
            color="inherit"
            component={RouterLink}
            to={reflectionArtifactPath(artifact, { coachShell })}
          >
            <div>
              <Typography className="font-semibold" variant="h6">
                {artifact.title ||
                  (isMemberReflectionProfile(artifact.profileSlug)
                    ? memberReflectionLabel(artifact.profileSlug)
                    : profileLabel(artifact.profileSlug))}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {sessionArtifactDescription(artifact.profileSlug)}
              </Typography>
            </div>
            <Icon color="action">chevron_right</Icon>
          </Link>
        ))}
      </div>
    </Paper>
  );
};

const SessionTranscripts = ({
  sessionTranscriptPath,
  transcripts,
}: {
  sessionTranscriptPath?: string | null;
  transcripts: MomentsSessionTranscriptSummary[];
}) => {
  if (transcripts.length === 0) {
    return null;
  }

  const readyTranscripts = readyTranscriptItems(transcripts);
  const hasReadyTranscriptLink =
    readyTranscripts.length > 0 && Boolean(sessionTranscriptPath);

  return (
    <Paper className="mt-6 px-8 pb-8 pt-6" variant="outlined">
      <Typography className="mb-4 font-bold" variant="h5">
        Session transcripts
      </Typography>
      {hasReadyTranscriptLink ? (
        <Link
          className="flex items-center justify-between rounded-xl border border-solid border-cement-200 px-4 py-3 no-underline"
          color="inherit"
          component={RouterLink}
          to={sessionTranscriptPath ?? ''}
        >
          <div>
            <Typography className="font-semibold" variant="h6">
              {readyTranscripts.length === 1
                ? 'Session transcript'
                : 'Session transcripts'}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              View your session transcript
            </Typography>
          </div>
          <Icon color="action">chevron_right</Icon>
        </Link>
      ) : (
        <div className="flex flex-col gap-3">
          {transcripts.map((transcript) => (
            <div
              key={transcript.attachmentId}
              className="rounded-xl border border-solid border-cement-200 px-4 py-3"
            >
              <Typography className="font-semibold" variant="h6">
                {transcript.displayName || 'Transcript'}
              </Typography>
              {transcript.contentStatus === 'ready' &&
              transcript.contentMarkdown?.trim() ? (
                <SlackishMarkdown text={transcript.contentMarkdown} />
              ) : (
                <Typography color="text.secondary" variant="body2">
                  {transcript.contentStatus === 'ready'
                    ? 'No transcript was recorded in this file.'
                    : `Transcript status: ${transcript.contentStatus || 'not ready'}`}
                </Typography>
              )}
            </div>
          ))}
        </div>
      )}
    </Paper>
  );
};

const Detail: FC<Props> = ({
  session,
  otherUser,
  isCoachViewing,
  loading = false,
  hideMenu = false,
  onViewProfile = undefined,
}) => {
  const { isCoach } = useView();
  const {
    currentData: currentUserData,
    isError: isCurrentUserError,
    isUserLoading,
    user,
  } = useCurrentUser();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [updateSession] = useUpdateSessionMutation();
  // Ops: neither the coach nor the booked member.
  const isThirdPartyViewer = !isCoachViewing && user.id !== session.userId;
  const isAdminViewer = isAdmin(user);
  // Whether the coach is on MentoCal is the coach's scheduling provider. The
  // viewing coach is the current user; admins can see Force Cancel from any
  // seat, including as the booked member, so Ops reads the session coach's
  // profile. `session.coach` is the minimal user view and has no coach profile.
  const sessionCoachProfileArg =
    !isCoachViewing && (isThirdPartyViewer || isAdminViewer) && session.coachId
      ? session.coachId
      : skipToken;
  const sessionCoachProfileQuery = useGetUserQuery(sessionCoachProfileArg);
  const isCoachOnMentoCal = usesMomentsScheduling(
    isCoachViewing ? user : sessionCoachProfileQuery.currentData,
  );
  const { showForceCancel, showSessionDetails } = calComControlsVisibility({
    isAdminViewer,
    isCoachCalendarKnown:
      isCoachViewing ||
      sessionCoachProfileArg === skipToken ||
      sessionCoachProfileQuery.currentData !== undefined ||
      sessionCoachProfileQuery.isError,
    isCoachOnMentoCal,
    isThirdPartyViewer,
  });
  const coachSessionActions = useSessionActions(user.profile?.timezone, {
    nativeMomentsActionScope:
      isCoachViewing && isCoachOnMentoCal ? 'recurring' : 'none',
    onAction: (event) => trackCoachSessionAction(event, isCoach),
  });
  const rescheduleFocusTargetRef = useRef<HTMLDivElement | null>(null);
  const [openCancel, setOpenCancel] = useState<Session | null>();
  const [openReschedule, setOpenReschedule] = useState<Session | null>();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [openForceCancel, setOpenForceCancel] = useState(false);
  const [warningAction, setWarningAction] =
    useState<SessionWarningAction | null>(null);
  const { id: memberId, memberSessionId } = useParams();
  const isDoneOrCancelled =
    session.status === SessionStatus.DONE ||
    session.status === SessionStatus.CANCELLED;
  const sessionLocation = useSessionJoinUrl(session, {
    enabled: !isDoneOrCancelled,
  });
  const hasLocation = !!sessionLocation;
  const canUseMemberNativeMomentsCancel = memberCanUseNativeMomentsCancel(
    user,
    session,
    isCoachViewing,
  );
  const canUseMemberNativeMomentsReschedule =
    memberCanUseNativeMomentsReschedule(user, session, isCoachViewing);
  // Native-capable members on a Moments session use the redesigned flows,
  // which own their own 48h speedbumps. Skipping the legacy SessionWarning
  // here avoids a double speedbump. Cal.com sessions keep SessionWarning.
  const isNativeMomentsSession =
    canUseMemberNativeMomentsCancel && isMomentsSession(session);
  const [searchParams] = useSearchParams();
  const { data: sharedSummary } = useGetSharedArtifactBySessionQuery(
    { session_id: session.id },
    { skip: !session.id },
  );
  const canReadSessionAssets =
    !!session.id &&
    !loading &&
    !isUserLoading &&
    !isCurrentUserError &&
    !!currentUserData &&
    currentUserData.id === user.id;
  const {
    currentData: momentsCoachMembers,
    isError: isCoachMembersError,
    isFetching: isCoachMembersFetching,
  } = useGetMomentsCoachMembersQuery(undefined, {
    skip: !canReadSessionAssets || !isCoachViewing,
  });
  const memberEmail = otherUser.email || session.user.email;
  const matchingMembers = momentsCoachMembers?.members.filter(
    (member) => findMomentsMemberByEmail([member], memberEmail) !== null,
  );
  const momentsMember =
    !isCoachMembersError &&
    !isCoachMembersFetching &&
    matchingMembers?.length === 1
      ? matchingMembers[0]
      : null;
  const canShowMomentsSessionAssets =
    canReadSessionAssets &&
    (isCoachViewing ? !!momentsMember?.personId : user.id === session.userId);
  const momentsMemberSessionAssetArg =
    canShowMomentsSessionAssets && !isCoachViewing
      ? { legacySessionId: session.id }
      : skipToken;
  const momentsCoachSessionAssetArg =
    canShowMomentsSessionAssets && isCoachViewing && momentsMember?.personId
      ? {
          legacySessionId: session.id,
          memberPersonId: momentsMember.personId,
        }
      : skipToken;
  const { currentData: momentsMemberArtifacts } =
    useGetMomentsSessionArtifactsQuery(momentsMemberSessionAssetArg);
  const { currentData: momentsCoachArtifacts } =
    useGetMomentsCoachSessionArtifactsQuery(momentsCoachSessionAssetArg);
  const { currentData: momentsTranscripts } =
    useGetMomentsSessionTranscriptsQuery(momentsMemberSessionAssetArg);
  const currentRoleArtifacts = isCoachViewing
    ? momentsCoachArtifacts
    : momentsMemberArtifacts;
  const momentsArtifacts = canShowMomentsSessionAssets
    ? currentRoleArtifacts
    : undefined;
  const memberTranscripts =
    momentsMemberSessionAssetArg !== skipToken ? momentsTranscripts : undefined;
  const momentsTranscriptPath = buildMemberSessionTranscriptPath({
    sessionDate: memberTranscripts?.session.plannedStartAt,
    sessionId: memberTranscripts?.session.sessionId,
  });
  const hasSharedSummary = !!sharedSummary;

  const selfSessionsBase = isCoachViewing ? '/coach/sessions' : '/m/sessions';
  const summaryPath =
    memberSessionId && memberId
      ? `/coach/members/${memberId}/sessions/${session.id}/summary`
      : `${selfSessionsBase}/${session.id}/summary`;

  const onCancelSession = () => {
    openSessionAction({
      coachAction: () => {
        coachSessionActions.openCancel(session).catch(() => undefined);
      },
      legacyAction: () => {
        if (!isNativeMomentsSession && sessionNeedsActionWarning(session)) {
          track('Shown Cancel Speedbump', {
            bookId: session.bookId,
            isCoach,
            sessionId: session.id,
          });
          setWarningAction(SessionWarningAction.CANCEL);
          return;
        }
        track('Clicked Cancel Session', {
          bookId: session.bookId,
          sessionId: session.id,
        });
        setOpenCancel(session);
      },
      useCoachAction: isCoachViewing,
    });
  };

  const onReschedule = (returnFocus?: () => void) => {
    openSessionAction({
      coachAction: () =>
        coachSessionActions.openReschedule(
          session,
          returnFocus ??
            (() =>
              rescheduleFocusTargetRef.current
                ?.querySelector<HTMLButtonElement>('button')
                ?.focus()),
        ),
      legacyAction: () => {
        if (
          !canUseMemberNativeMomentsReschedule &&
          sessionNeedsActionWarning(session)
        ) {
          track('Shown Reschedule Speedbump', {
            bookId: session.bookId,
            isCoach,
            sessionId: session.id,
          });
          setWarningAction(SessionWarningAction.RESCHEDULE);
          return;
        }
        track('Click Reschedule Session', {
          bookId: session.bookId,
          sessionId: session.id,
        });
        setOpenReschedule(session);
      },
      useCoachAction: isCoachViewing,
    });
  };
  useEffect(() => {
    if (session.id && searchParams.has('action')) {
      const action = searchParams.get('action');
      if (action === SessionAction.RESCHEDULE) {
        onReschedule();
      } else if (action === SessionAction.CANCEL) {
        onCancelSession();
      }
    }
  }, [searchParams, session.id]);
  const onCloseModal = () => {
    dispatch(api.util.invalidateTags(['Session']));
    setOpenCancel(null);
    setOpenReschedule(null);
  };

  const onCancelled = () => {
    if (!isCoachViewing) {
      navigate('/m/sessions/upcoming', {
        state: { sessionCancelled: true },
      });
      return;
    }

    setShowCancelConfirmation(true);
  };

  const onCloseWarning = () => setWarningAction(null);

  const handleForceCancel = async () => {
    try {
      await updateSession({
        data: { status: SessionStatus.CANCELLED },
        id: session.id,
      });
      dispatch(api.util.invalidateTags(['Session']));
      setOpenForceCancel(false);
    } catch (error) {
      console.error('Error force canceling session:', error);
    }
  };

  return (
    <>
      {showCancelConfirmation && (
        <Alert severity="success">Session cancelled.</Alert>
      )}
      <Paper className="my-6 flex gap-4 px-8 pb-12 pt-8" variant="outlined">
        <div className="!border-sunny-dark flex w-full border bg-white">
          <div className="flex flex-1 flex-col">
            <Header
              hideMenu={hideMenu}
              isCoachViewing={isCoachViewing}
              loading={loading}
              session={session}
              showSummaryLink={hasSharedSummary}
              summaryPath={summaryPath}
              user={otherUser}
              onCancelSession={onCancelSession}
              onReschedule={onReschedule}
              onViewProfile={onViewProfile}
            />
          </div>
        </div>
      </Paper>

      <Notes
        limit={3}
        owneableId={session.id}
        owneableType={NoteOwnerType.SESSION}
      />

      <SessionArtifacts
        artifacts={momentsArtifacts?.items ?? []}
        coachShell={isCoachViewing}
      />
      <SessionTranscripts
        sessionTranscriptPath={momentsTranscriptPath}
        transcripts={memberTranscripts?.items ?? []}
      />

      {showSessionDetails && !isDoneOrCancelled && (
        <Paper
          className="mt-6 flex flex-col gap-8 px-8 pb-12 pt-8 md:flex-row"
          variant="outlined"
        >
          <div className="flex-1">
            <Typography className="mb-4 select-none font-bold" variant="h5">
              Session details
            </Typography>
            <Typography variant="subtitle1">Need to make a change?</Typography>
            <Typography variant="body1">
              Please reschedule or cancel 48 hours before the session start
              time. <br />
              <a
                className="text-sage-500 underline"
                href="https://mentoteam.notion.site/Mento-Rescheduling-Cancelation-Policy-ea6ed8fa23fc41a8a4598070ef42fb53"
                rel="noopener noreferrer"
                target="_blank"
              >
                Rescheduling & Cancelation Policy.
              </a>
            </Typography>
          </div>
          <div
            className={clsx('flex  basis-7/12 flex-col gap-4', {
              'justify-between': hasLocation,
              'justify-end': !hasLocation,
            })}
          >
            {sessionLocation ? (
              <div className="flex items-center rounded-xl bg-sage-200 p-4">
                <Icon>videocam</Icon>

                <div className="flex flex-col pl-2">
                  <Typography>Session Location</Typography>
                  <Link
                    className="text-xs text-cement-400"
                    href={sessionLocation}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {sessionLocation}
                  </Link>
                </div>
              </div>
            ) : null}
            <div
              ref={rescheduleFocusTargetRef}
              className="flex justify-end gap-4"
            >
              <Button variant="outlined" onClick={() => onReschedule()}>
                Reschedule
              </Button>
              <Button variant="outlined" onClick={onCancelSession}>
                Cancel
              </Button>
            </div>
          </div>
        </Paper>
      )}
      {!!warningAction && (
        <SessionWarning
          action={warningAction}
          close={onCloseWarning}
          session={session}
          onCancel={setOpenCancel}
          onReschedule={setOpenReschedule}
        />
      )}
      {!!openCancel && (
        <CancelSession
          allowMemberMomentsCancellation={canUseMemberNativeMomentsCancel}
          close={onCloseModal}
          session={openCancel}
          onCancelled={onCancelled}
          onRescheduleInstead={() => {
            // Hand off to reschedule for the same session without reloading
            // (no mutation happened). Capture the session before nulling.
            const next = openCancel;
            setOpenCancel(null);
            setOpenReschedule(next);
          }}
        />
      )}
      {!!openReschedule && (
        <RescheduleSession
          close={onCloseModal}
          enableNativeMoments={canUseMemberNativeMomentsReschedule}
          session={openReschedule}
          onRescheduled={() => navigate(selfSessionsBase)}
        />
      )}
      {coachSessionActions.modals}
      {showForceCancel && !isDoneOrCancelled && (
        <Button
          className="mt-4"
          color="warning"
          variant="outlined"
          onClick={() => setOpenForceCancel(true)}
        >
          Force Cancel Session
        </Button>
      )}
      <ForceCancelDialog
        open={openForceCancel}
        onClose={() => setOpenForceCancel(false)}
        onConfirm={handleForceCancel}
      />
    </>
  );
};

export default Detail;
